/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Shared robots.txt constants used by both Next.js apps and Cloudflare Workers.
 *
 * This file is intentionally kept separate from robots.ts (which imports
 * @cosmos/common-utils and reads env vars at access time) so the Workers runtime can
 * consume it — same split as utils/sitemap.constants.ts. Keep it dependency-free.
 */

/** Paths and query-string patterns no crawler should index. */
export const disallowedPaths = [
	//root no locale paths to disallow
	"/portal*",
	"/register*",
	"/account*",
	"/dashboard*",
	"/id*",
	"/v1/developers*",
	"/internal/*",

	//locale based paths to disallow
	"/*/try-free*",
	"/*/search",

	//disallow urls that have params
	"/*?tags=",
	"/*&tags=",
	"/*?filter=",
	"/*&filter=",
	"/*?keyword=",
	"/*&keyword=",
	"/*?skip=",
	"/*&skip=",
] as const;

/**
 * AI search & retrieval crawlers, explicitly allowed org-wide (MW-12789).
 *
 * These agents power answer engines and user-initiated fetches — being indexed by them
 * is search visibility, not training-corpus inclusion. Every domain already allowed them
 * implicitly via the `*` group; naming them makes that intentional rather than incidental.
 *
 * The training-reservation counterpart is {@link aiTrainingUserAgents} + {@link CONTENT_SIGNAL},
 * gated per brand behind `reserveAiTraining` (see robots.ts) because reserving training rights
 * is a per-brand legal decision. With the gate closed — the default — those agents keep falling
 * under the `*` group unchanged.
 *
 * Note on Google: there is no search-only opt-in user agent to add. `Googlebot` (already
 * covered by `*`) drives Search; `Google-Extended` is a training/grounding control only
 * and has no effect on Search indexing.
 *
 * NEVER name `Googlebot`, `bingbot` or `Applebot` in any group here. Absence is the safe state:
 * naming an agent detaches it from `*`, and for Bing it makes Bingbot ignore every other
 * directive in the file. Apple additionally documents that if robots instructions don't mention
 * Applebot but do mention Googlebot, Applebot follows the Googlebot rules — so adding a
 * `Googlebot` group would silently capture Applebot too.
 *
 * Known omissions, deliberate and reviewed (docs/drafts/robots-ai-policy/findings-taxonomy.md):
 * `Meta-WebIndexer` (Meta's citation crawler), `Amzn-SearchBot`, `Amzn-User`, `MistralAI-Index`,
 * `MistralAI-User` and `DuckAssistBot` are all search/citation agents that belong here on
 * intent. They are omitted only because naming them changes nothing functionally — they are
 * already allowed via `*`, and `*` also carries the content signal — so adding them is a
 * clarity-only edit best made on its own, not bundled with a legally-gated change.
 *
 * Note on Quixel (MW-12789 "reconcile Quixel/Fab"): quixel.com currently serves an AI
 * training block wrapped in `# BEGIN/END Cloudflare Managed content` — it is injected by
 * Cloudflare at the zone level, NOT by this config and NOT by the legacy origin. Nothing
 * here changes it. Note also that `/robots.txt` is absent from the legacy-only route list
 * in cloudflare-routers/quixel-website/src/routes.ts, so when the `quixel-cosmos` flag is
 * on the origin response for quixel.com/robots.txt does come from this config — it is the
 * Cloudflare managed layer in front that determines what crawlers actually see.
 */
export const aiSearchUserAgents = [
	"OAI-SearchBot", //OpenAI — ChatGPT search index
	"ChatGPT-User", //OpenAI — user-initiated fetch
	"PerplexityBot", //Perplexity — search index
	"Perplexity-User", //Perplexity — user-initiated fetch
	"Claude-SearchBot", //Anthropic — search index
	"Claude-User", //Anthropic — user-initiated fetch
] as const;

/**
 * AI training / corpus-collection crawlers, blocked outright when a brand opts in to reserving
 * training rights. Emitted as a single group with a bare `Disallow: /` — a strict superset of
 * `disallowedPaths`, so unlike the search group this one needs no copy of that list, and no
 * parser has to resolve any Allow/Disallow precedence to honour it.
 *
 * OFF BY DEFAULT. Enabling is a per-brand legal decision — see `reserveAiTraining` in robots.ts.
 *
 * Every token here is classified from the vendor's own documentation; the full table, including
 * the tokens deliberately excluded, is in docs/drafts/robots-ai-policy/findings-taxonomy.md.
 *
 * Each entry is a WHOLE product token. Group matching is exact, case-insensitive token equality
 * (RFC 9309 §2.2.1) — NOT prefix matching — which is what makes the near-miss pairs safe:
 * blocking `Applebot-Extended` cannot catch `Applebot`, and blocking `ClaudeBot` cannot catch
 * `Claude-SearchBot` or `Claude-User`. Never write a partial token or a `*` suffix here.
 *
 * Two of these do not crawl at all: `Google-Extended` and `Applebot-Extended` are pure usage
 * opt-outs with zero crawl or Search impact, which is precisely why they are safe to include.
 *
 * ⚠️ `Google-Extended` does NOT opt out of Google's AI Overviews or AI Mode. Those are fed by
 * `Googlebot` and have no separate token, so that exclusion is not achievable via robots.txt.
 * Likewise Copilot: it derives from the Bing index and has no user agent — Microsoft's lever is
 * the page-level `<meta name="robots" content="noarchive">` tag, not this file.
 *
 * Reviewed and deliberately EXCLUDED: `Amazonbot` (dual-purpose — also improves Amazon products;
 * a defensible block, pending decision), `CloudflareBrowserRenderingCrawler` (Cloudflare's own
 * renderer, not a trainer — blocking it can break Browser Rendering), `ImagesiftBot` (silent on
 * training; block would rest on inference), `Webzio`/`Webzio-Extended` and `GoogleOther`.
 */
export const aiTrainingUserAgents = [
	"GPTBot", //OpenAI — foundation-model training
	"ClaudeBot", //Anthropic — model training corpus
	"CCBot", //Common Crawl — open corpus widely used as training data
	"Google-Extended", //Google — Gemini training/grounding control; does not crawl
	"Applebot-Extended", //Apple — training opt-out control; does not crawl
	"Bytespider", //ByteDance — undocumented; credible reports of robots.txt non-compliance
	"meta-externalagent", //Meta — foundation-model training
	"MistralAI-Training", //Mistral — training-dataset collection
] as const;

/**
 * Cloudflare Content Signals Policy declaration (https://contentsignals.org/), asserting that
 * search indexing and reference-style citation are permitted while AI training is not.
 *
 * `ai-input` is deliberately left unset: omission is explicitly neutral under the policy
 * ("neither grants nor restricts permission"), and Cloudflare's own managed file leaves it unset
 * rather than guessing intent. `use` is documented as experimental.
 *
 * Formatted to match Cloudflare's LIVE emitted output — no spaces after the commas. (Its blog
 * example uses spaces; the file quixel.com actually serves does not.)
 *
 * This is a declaration and a rights reservation, not enforcement: robots.txt compliance is
 * voluntary and nothing here prevents access at a technical level. Enforcement is a
 * WAF/bot-management concern — see the `cloudflare_bot_management` resources under
 * `terraform/cloudflare-zones`.
 *
 * Group-scoped like a rule (RFC 9309 §2.2.4): a signal in the `*` group is invisible to any
 * agent that has its own group, so it must be repeated in the search/citation group.
 */
export const CONTENT_SIGNAL = "search=yes,ai-train=no,use=reference";

/**
 * THE SWITCH. Flip to `true` to reserve AI-training rights across every Cosmos-served domain:
 * each site's robots.txt then gains the Content Signals preamble, a `Content-Signal` line on both
 * crawlable groups, and a `Disallow: /` group for {@link aiTrainingUserAgents}.
 *
 * ⚠️ DO NOT FLIP WITHOUT LEGAL SIGN-OFF. MW-12938 gates the training-reservation half on it. The
 * preamble asserts a condition of access and an express Article 4 reservation — withdrawing one
 * that crawlers have already observed is messier than adding it later, so `false` is the safe
 * default and the reason this ships as one line rather than fourteen.
 *
 * Every consumer defaults to this: the 14 site route handlers and the epicgames edge worker. A
 * single brand can still opt in ahead of the rest by passing `reserveAiTraining: true` explicitly
 * at its own call site.
 */
export const RESERVE_AI_TRAINING = false;

/**
 * The Content Signals Policy preamble, published by Cloudflare under CC0 and reproduced verbatim
 * (https://contentsignals.org/). Emitted as `#` comments above the groups.
 *
 * Comments have no effect on any parser — a crawler that doesn't implement Content Signals
 * ignores the `Content-Signal:` line and the comments alike. The preamble exists for the human
 * audience the reservation is actually aimed at: counsel, regulators, and crawler operators
 * auditing why they were blocked. `ai-train=no` on its own is a bare string whose meaning lives
 * on a third-party site; this text makes the reservation self-defining, which is what turns it
 * into strong evidence of an express Article 4 reservation rather than weak evidence.
 *
 * For that reason it is emitted ONLY alongside {@link CONTENT_SIGNAL} — see `buildRobotsTxt`.
 * Shipping the directive without the definitions is the weak combination: it takes on the legal
 * assertion while giving up most of its evidentiary value.
 *
 * NOTE: the opening line is a condition-of-access assertion, not a preference, and the closing
 * paragraph is an express reservation of rights. Whether Epic asserts this wording — or its own —
 * is a decision for counsel, not a code review.
 */
export const CONTENT_SIGNAL_PREAMBLE = `# As a condition of accessing this website, you agree to abide by the following
# content signals:

# (a)  If a Content-Signal = yes, you may collect content for the corresponding
#      use.
# (b)  If a Content-Signal = no, you may not collect content for the
#      corresponding use.
# (c)  If the website operator does not include a Content-Signal for a
#      corresponding use, the website operator neither grants nor restricts
#      permission via Content-Signal with respect to the corresponding use.

# The content signals and their meanings are:

# search:   building a search index and providing search results (e.g., returning
#           hyperlinks and short excerpts from your website's contents). Search does not
#           include providing AI-generated search summaries.
# ai-input: inputting content into one or more AI models (e.g., retrieval
#           augmented generation, grounding, or other real-time taking of content for
#           generative AI search answers).
# ai-train: training or fine-tuning AI models.
# use:      how AI systems may consume the content (immediate, reference, or full).

# ANY RESTRICTIONS EXPRESSED VIA CONTENT SIGNALS ARE EXPRESS RESERVATIONS OF
# RIGHTS UNDER ARTICLE 4 OF THE EUROPEAN UNION DIRECTIVE 2019/790 ON COPYRIGHT
# AND RELATED RIGHTS IN THE DIGITAL SINGLE MARKET.
`;
